/**
 * デザイン view の子票行を「決着済み（= これ以上 PL が触る必要がない）」かどうかで
 * 絞り込む純粋関数群。
 *
 * デザイン view は未完了デザイン子がある母票を出すため、終わった子票も同じ母票の下に
 * 残り続ける。アンケートまで回答し終えた子票は PL の行動が残っていないので、
 * 直後の確認期間（1 週間）だけ残して以後は一覧から落とす。
 * **未回答は 1 件も落とさない**のが唯一の不変条件 — 落とすと回答経路が消える。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business デザイン view の担当案件一覧から「完了/処理済 + 回答済み（または mypage で回答できない旧案件）」の子票行を 1 週間経過後に除外する
 * @graph-connects isPastRetention [calls] 決着日からの保持期間判定
 */
import type { EvaluationTask } from '../../../types/idt-design-evaluation.js';
import type { DesignChildTicket } from '../../../types/project.js';
import { isPastRetention } from './settled-row-retention.js';

// 保持期間（1 週間）と東京日付は DLE 評価アンケートの完了母票行と共通のため
// `settled-row-retention.ts` に置いた。既存の import 元としてここから re-export する。
export { SETTLED_ROW_RETENTION_DAYS, tokyoToday } from './settled-row-retention.js';

/**
 * 決着扱いにする子票ステータス。
 * 「完了」(status.id 4) のほか、運用上そのまま終わらせる「処理済」(status.id 3) も含める。
 * @graph-connects none
 */
const SETTLED_CHILD_STATUSES: readonly string[] = ['完了', '処理済'];

/**
 * 子票が決着した日（"YYYY-MM-DD"）。判定材料が無ければ null。
 *
 * 起点は完了更新日（CF112302）。欠けている旧子票は最終更新日で代替する。
 * 回答がそれより後なら回答日を採る — 回答した直後に行が消えると、
 * 自分の回答が通ったことを一覧で確認できない。
 * @graph-connects none
 */
export function settledOn(child: DesignChildTicket, task: EvaluationTask | null): string | null {
  const completed = child.completedDate ?? child.updatedDate;
  const responded = task?.state === 'answered' ? (task.respondedAt?.slice(0, 10) ?? null) : null;
  if (completed === null) return responded;
  if (responded === null) return completed;
  return responded > completed ? responded : completed;
}

/**
 * この子票行を一覧から落とすか。
 *
 * 落とす条件は 3 つすべて:
 * 1. ステータスが完了 / 処理済
 * 2. アンケートに PL の行動が残っていない（回答済み、または mypage で回答できない対象外 = task なし）
 * 3. 決着日から `SETTLED_ROW_RETENTION_DAYS` を超えて経過している
 *
 * 決着日が取れない子票は落とさない（判定材料が無いものを黙って消さない）。
 * @graph-connects none
 */
export function isStaleSettledChild(
  child: DesignChildTicket,
  task: EvaluationTask | null,
  today: string,
): boolean {
  if (!SETTLED_CHILD_STATUSES.includes(child.status)) return false;
  if (task !== null && task.state !== 'answered') return false;
  return isPastRetention(settledOn(child, task), today);
}

/**
 * 一覧に出す子票だけを残す。
 * @graph-connects isStaleSettledChild [calls] 子票 1 件の除外判定
 */
export function visibleDesignChildren(
  children: DesignChildTicket[],
  taskByChild: ReadonlyMap<string, EvaluationTask>,
  today: string,
): DesignChildTicket[] {
  return children.filter(
    (child) => !isStaleSettledChild(child, taskByChild.get(child.issueKey) ?? null, today),
  );
}
